import { useState, useEffect } from 'react';
import ProjectSelector from './ProjectSelector';
import { API_BASE_URL } from '../config';

interface CollectionSelectorProps {
  appToken: string | null;
  selected: string | null;
  activeView?: string;
  onSettings?: () => void;
  onSelect: (id: string | null, name: string | null) => void;
  onSiteResolved?: (siteId: string, shortName: string, workspaceId?: string) => void;
  onAuthError?: () => void;
  userProfile?: { installations?: any[], isPremium: boolean, freePlanCollectionId?: string | null, freeItemsUsed?: number, invitedWorkspaces?: any[] } | null;
  onUpgradeRequest?: () => void;
  refreshProfile?: () => void;
}

export default function CollectionSelector({ appToken, selected, activeView, onSettings, onSelect, onSiteResolved, onAuthError, userProfile }: CollectionSelectorProps) {
  const [collections, setCollections] = useState<any[]>([]);
  const [availableSites, setAvailableSites] = useState<any[]>([]);
  const [activeSiteId, setActiveSiteId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [issuesCount, setIssuesCount] = useState<number>(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    async function initSites() {
      if (!appToken) return;
      
      try {
        const res = await fetch(`${API_BASE_URL}/api/inspect/sites`, {
          headers: { 'Authorization': `Bearer ${appToken}` }
        });
        
        if (res.status === 401) {
          if (onAuthError) onAuthError();
          return;
        }
        
        const data = await res.json();
        const allSites = data.sites || [];
        
        if (typeof window !== 'undefined' && typeof window.webflow !== 'undefined') {
          // If in iframe Designer, get siteInfo
          const siteInfo = await window.webflow.getSiteInfo();
          setActiveSiteId(siteInfo.siteId);
          if (onSiteResolved) onSiteResolved(siteInfo.siteId, siteInfo.shortName, siteInfo.workspaceId);
          
          if (allSites.length > 0) {
            setAvailableSites(allSites);
            const matchingSite = allSites.find((s: any) => s.id === siteInfo.siteId);
            if (!matchingSite && onAuthError) {
              // The user is in a Webflow Designer for a site they haven't authorized locally
              // Or maybe they just need to authorize it
            }
          }
        } else {
          // Standalone app
          if (allSites.length > 0) {
            setAvailableSites(allSites);
            const urlParams = new URLSearchParams(window.location.search);
            const targetSiteId = urlParams.get('siteId');
            
            let site = allSites.find((s: any) => s.id === targetSiteId);
            if (!site) site = allSites[0];

            setActiveSiteId(site.id);
            if (onSiteResolved) onSiteResolved(site.id, site.shortName, site.workspaceId);
          } else {
            setAvailableSites([]);
            setIsLoading(false);
          }
        }
      } catch (err: any) {
        setErrorDetails(err.message);
        setIsLoading(false);
      }
    }
    initSites();
  }, [appToken, refreshTrigger]);

  useEffect(() => {
    if (!activeSiteId || !appToken || availableSites.length === 0) return;
    
    const site = availableSites.find(s => s.id === activeSiteId);
    if (!site) return;
    
    async function loadCollections() {
      setIsLoading(true);
      setErrorDetails(null);
      try {
        const colRes = await fetch(`${API_BASE_URL}/api/inspect/collections/${activeSiteId}`, {
          headers: { 'Authorization': `Bearer ${appToken}` }
        });
        
        if (colRes.status === 401) {
          if (onAuthError) onAuthError();
          return;
        }
        
        const colData = await colRes.json();
        
        if (colData.collections) {
           setCollections(colData.collections);
        } else {
           setErrorDetails(`Failed to get collections: ${JSON.stringify(colData)}`);
        }
      } catch (err: any) {
        setErrorDetails(err.message);
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    loadCollections();
  }, [activeSiteId, appToken, availableSites]);

  useEffect(() => {
    if (!appToken || !activeSiteId) return;
    const fetchIssuesCount = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/config/issues-count?siteId=${activeSiteId}`, {
          headers: { 'Authorization': `Bearer ${appToken}` }
        });
        const data = await res.json();
        setIssuesCount(data.count || 0);
      } catch (err) {}
    };
    fetchIssuesCount();
    const interval = setInterval(fetchIssuesCount, 30000);
    return () => clearInterval(interval);
  }, [appToken, activeSiteId]);

  const [searchTerm, setSearchTerm] = useState('');
  const filteredCollections = collections.filter(col => 
    (col.displayName || col.name).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full">
      <div className="p-3">
        {(availableSites.length > 0 || (userProfile?.installations && userProfile.installations.length > 0) || (userProfile?.invitedWorkspaces && userProfile.invitedWorkspaces.length > 0)) && (
          <div className="mb-3">
            <ProjectSelector 
              sites={availableSites} 
              activeSiteId={activeSiteId} 
              onChange={(newId) => {
                setActiveSiteId(newId);
                const s = availableSites.find(x => x.id === newId);
                if (s) {
                  if (onSiteResolved) onSiteResolved(s.id, s.shortName, s.workspaceId);
                }
                onSelect(null, null); // switch back to Home when site changes
              }}
              onAuthSuccess={() => setRefreshTrigger(prev => prev + 1)}
              token={appToken}
              installations={userProfile?.installations || []}
              userProfile={userProfile}
            />
          </div>
        )}
        <div className="relative">
          <input
            aria-label="Filter collections"
            type="text"
            placeholder="Filter collections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[var(--background1)] border border-[var(--border1)] text-[var(--text2)] text-[12px] md:text-[13px] rounded px-3 py-1.5 focus:outline-none focus:border-[#555] transition-colors"
          />
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto px-2 pb-4">
        {isLoading ? (
          <p className="text-[12px] md:text-[13px] text-[var(--text3)] px-2 py-1">Loading collections...</p>
        ) : errorDetails ? (
          <div className="text-[11px] md:text-[12px] text-white bg-[var(--redBackground)] p-2 rounded mx-2">
            <p className="font-bold">API Error:</p>
            <pre className="whitespace-pre-wrap mt-1 text-[10px] md:text-[11px]">{errorDetails}</pre>
          </div>
        ) : (
          <div className="flex flex-col space-y-[2px]">
            <button
              onClick={() => onSelect(null, null)}
              className={`flex items-center px-2 py-1.5 rounded text-left transition text-[13px] md:text-[14px] group mt-1 ${
                selected === null && activeView !== 'settings'
                  ? 'bg-[var(--background3)] text-[var(--blueText)]'
                  : 'bg-transparent hover:bg-[var(--background2)] text-[var(--text2)]'
              }`}
            >
              <div className="flex flex-1 items-center">
                <svg className={`w-3.5 h-3.5 mr-2 ${selected === null && activeView !== 'settings' ? 'text-[var(--blueText)]' : 'text-[var(--text3)] group-hover:text-[#aaa]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Issues</span>
              </div>
              {issuesCount > 0 && (
                <span className="bg-red-500/20 text-red-400 border border-red-500/30 text-[10px] md:text-[11px] font-bold px-1.5 py-0.5 rounded-sm">
                  {issuesCount}
                </span>
              )}
            </button>
            <div className="text-[11px] md:text-[12px] font-semibold text-[var(--text3)] uppercase tracking-wider px-2 py-1 mt-3 mb-1">
              CMS Collections
            </div>
            {availableSites.length === 0 ? (
              <div className="px-2 py-2 text-[12px] md:text-[13px] text-[var(--text3)] bg-[var(--background2)] rounded border border-[var(--border1)] text-center my-2">
                No Webflow workspaces connected.<br/><br/>
                Please connect one from Settings to get started.
              </div>
            ) : filteredCollections.map(col => {
              return (
              <button
                key={col.id}
                onClick={() => onSelect(col.id, col.displayName || col.name)}
                className={`flex items-center px-2 py-1.5 rounded text-left transition text-[13px] md:text-[14px] group ${
                  selected === col.id && activeView !== 'settings'
                    ? 'bg-[var(--background3)] text-[var(--blueText)]' 
                    : 'bg-transparent hover:bg-[var(--background2)] text-[var(--text2)]'
                }`}
              >
                <svg className={`w-3.5 h-3.5 mr-2 ${selected === col.id && activeView !== 'settings' ? 'text-[var(--blueText)]' : 'text-[var(--text3)] group-hover:text-[#aaa]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span className="font-medium truncate">{col.displayName || col.name}</span>
              </button>
            )})}
            {availableSites.length > 0 && filteredCollections.length === 0 && (
              <p className="text-[12px] md:text-[13px] text-[#666] px-2 py-1">No collections found.</p>
            )}
            
            <div className="mt-4 pt-2 border-t border-[#2a2a2a]">
              <button
                onClick={() => { if (onSettings) onSettings() }}
                className={`flex items-center px-2 py-1.5 rounded text-left transition text-[13px] md:text-[14px] w-full group ${
                  activeView === 'settings'
                    ? 'bg-[var(--background3)] text-[var(--blueText)]'
                    : 'bg-transparent hover:bg-[var(--background2)] text-[var(--text2)]'
                }`}
              >
                <svg className={`w-3.5 h-3.5 mr-2 ${activeView === 'settings' ? 'text-[var(--blueText)]' : 'text-[var(--text3)] group-hover:text-[#aaa]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="font-medium">Settings</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
